import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useAnimationControls, useReducedMotion } from "framer-motion";

export type BuddyFocus = "none" | "name" | "email" | "password";

export interface PasswordChecks {
  minLength: boolean;
  upperAndLower: boolean;
  symbol: boolean;
  number: boolean;
}

interface SignupBuddiesProps {
  focus: BuddyFocus;
  name: string;
  email: string;
  password: string;
  showPassword: boolean;
  checks: PasswordChecks;
  agreedToTerms?: boolean;
  isCompany?: boolean;
}

type Mood = "happy" | "neutral" | "worried" | "delighted";
type Look = { x: number; y: number };
type Pointer = { x: number; y: number; at: number } | null;

const isValidEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value.trim());

const MISSING_HINTS: [keyof PasswordChecks, string][] = [
  ["minLength", "make it 8+ characters"],
  ["upperAndLower", "mix upper & lower case"],
  ["number", "add a number"],
  ["symbol", "add a symbol"],
];

// Mouth and brow shapes share the same path commands so framer-motion can morph them.
const MOUTHS: Record<Mood, string> = {
  happy: "M43 61 Q50 66 57 61",
  delighted: "M41 60 Q50 70 59 60",
  neutral: "M44 63 Q50 63 56 63",
  worried: "M44 64.5 Q50 59.5 56 64.5",
};
const BROWS: Record<Mood, [string, string]> = {
  happy: ["M35 36 Q40.5 33.5 46 35.5", "M54 35.5 Q59.5 33.5 65 36"],
  delighted: ["M35 34 Q40.5 31 46 33.5", "M54 33.5 Q59.5 31 65 34"],
  neutral: ["M35 36 Q40.5 35.5 46 36", "M54 36 Q59.5 35.5 65 36"],
  worried: ["M35 36 Q40.5 35 46 33", "M54 33 Q59.5 35 65 36"],
};

const PALETTE = {
  woman: { bg: "#E6EDFB", skin: "#7A4A2E", hair: "#1F1A17", shirt: "#F6C744", lips: "#8E2B3A" },
  man: { bg: "#FFF4D6", skin: "#A86B45", hair: "#2A211C", shirt: "#143887", lips: "#2A1A14" },
};

interface CharacterProps {
  variant: "woman" | "man";
  mood: Mood;
  pointer: Pointer;
  textLook: Look | null; // where to look while typing (null = no text field focused)
  preferText: boolean; // typing happened more recently than the mouse moved
  covering: boolean;
  peeking: boolean;
  nod: number; // increments on every keystroke
  reduceMotion: boolean;
}

const Character: React.FC<CharacterProps> = ({ variant, mood, pointer, textLook, preferText, covering, peeking, nod, reduceMotion }) => {
  const c = PALETTE[variant];
  const clipId = `buddy-clip-${variant}`;
  const ref = useRef<HTMLDivElement>(null);
  const [blink, setBlink] = useState(false);
  const [pointerLook, setPointerLook] = useState<Look>({ x: 0, y: 0 });
  const head = useAnimationControls();

  // Blink every few seconds (offset per character so they don't blink in sync).
  useEffect(() => {
    if (reduceMotion) return;
    let timer: ReturnType<typeof setTimeout>;
    const schedule = () => {
      timer = setTimeout(() => {
        setBlink(true);
        timer = setTimeout(() => {
          setBlink(false);
          schedule();
        }, 140);
      }, 2600 + Math.random() * 2400 + (variant === "man" ? 700 : 0));
    };
    schedule();
    return () => clearTimeout(timer);
  }, [reduceMotion, variant]);

  // Each character looks at the pointer from its own position.
  useEffect(() => {
    if (!pointer || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    const dx = pointer.x - (r.left + r.width / 2);
    const dy = pointer.y - (r.top + r.height * 0.45);
    const dist = Math.hypot(dx, dy) || 1;
    const reach = Math.min(dist / 120, 1); // pointer near the face = small movement
    setPointerLook({ x: (dx / dist) * 2.2 * reach, y: (dy / dist) * 2.4 * reach });
  }, [pointer]);

  // A small nod on each keystroke.
  useEffect(() => {
    if (reduceMotion || nod === 0) return;
    head.start({ rotate: [0, variant === "woman" ? -4 : 4, 0], transition: { duration: 0.25 } });
  }, [nod, head, reduceMotion, variant]);

  const look = textLook && preferText ? textLook : pointerLook;
  const t = reduceMotion ? { duration: 0 } : { type: "spring" as const, stiffness: 260, damping: 20 };
  const eyeT = reduceMotion ? { duration: 0 } : { type: "spring" as const, stiffness: 420, damping: 28 };
  // Hands rise from below the circle to cover the eyes; when peeking, one hand drops a little.
  const handY = (side: "l" | "r") => (!covering ? 64 : peeking && side === "l" ? 15 : 0);
  const isWoman = variant === "woman";

  return (
    <motion.div ref={ref} className="w-full h-full" animate={head} style={{ originY: 0.9 }}>
      <svg viewBox="0 0 100 100" className="w-full h-full" aria-hidden="true">
        <defs>
          <clipPath id={clipId}>
            <circle cx="50" cy="50" r="50" />
          </clipPath>
        </defs>
        <circle cx="50" cy="50" r="50" fill={c.bg} />
        <g clipPath={`url(#${clipId})`}>
          {/* woman: hair falls behind the head to the shoulders */}
          {isWoman && <path d="M25 44 Q22 70 30 80 L70 80 Q78 70 75 44 Q70 26 50 24 Q30 26 25 44 Z" fill={c.hair} />}
          {/* body, collar / necklace, neck */}
          <path d="M14 100 Q18 76 50 74 Q82 76 86 100 Z" fill={c.shirt} />
          {isWoman ? (
            <path d="M40 75 Q50 84 60 75" stroke="#E0A800" strokeWidth="2" fill="none" />
          ) : (
            <path d="M42 75 L50 84 L58 75 L54 74 L50 78 L46 74 Z" fill="#FFFFFF" opacity="0.9" />
          )}
          <rect x="44" y="60" width="12" height="16" rx="4" fill={c.skin} />
          {/* ears (+ earrings) */}
          <ellipse cx="30.5" cy="48" rx="3.5" ry="5" fill={c.skin} />
          <ellipse cx="69.5" cy="48" rx="3.5" ry="5" fill={c.skin} />
          {isWoman && (
            <>
              <circle cx="30" cy="55" r="2.2" fill="#F6C744" stroke="#C98F00" strokeWidth="0.6" />
              <circle cx="70" cy="55" r="2.2" fill="#F6C744" stroke="#C98F00" strokeWidth="0.6" />
            </>
          )}
          {/* head */}
          <ellipse cx="50" cy="46" rx="19.5" ry="22" fill={c.skin} />
          {/* hair on top: woman = puff + hairline, man = short cut */}
          {isWoman ? (
            <g fill={c.hair}>
              <circle cx="50" cy="15" r="9" />
              <path d="M30 42 Q30 22 50 22 Q70 22 70 42 Q63 30 50 30 Q37 30 30 42 Z" />
            </g>
          ) : (
            <path d="M30 41 Q29 22 50 21.5 Q71 22 70 41 Q67 29 50 29.5 Q35 29.5 30 41 Z" fill={c.hair} />
          )}
          {/* man: beard + moustache */}
          {!isWoman && (
            <g fill={c.hair}>
              <path d="M31 47 Q31 68 50 72 Q69 68 69 47 Q67 63 58 65 Q50 67 42 65 Q33 63 31 47 Z" />
              <path d="M42 58.5 Q50 55 58 58.5 Q50 57.5 42 58.5 Z" />
            </g>
          )}
          {/* cheeks */}
          <circle cx="37" cy="54" r="3.4" fill="#FF8A8A" opacity={mood === "delighted" ? 0.5 : 0.22} />
          <circle cx="63" cy="54" r="3.4" fill="#FF8A8A" opacity={mood === "delighted" ? 0.5 : 0.22} />
          {/* brows */}
          {BROWS[mood].map((d, i) => (
            <motion.path key={i} initial={false} animate={{ d }} transition={t} stroke="#1B1B1B" strokeWidth="2" strokeLinecap="round" fill="none" />
          ))}
          {/* eyes: whites + pupils that follow `look`; the group squashes to blink */}
          <motion.g initial={false} animate={{ scaleY: blink && !covering ? 0.08 : 1 }} transition={{ duration: 0.08 }} style={{ originY: "45px" }}>
            {[41, 59].map((cx) => (
              <g key={cx}>
                <ellipse cx={cx} cy="45" rx="4.6" ry="5.2" fill="#FFFFFF" />
                <motion.g initial={false} animate={{ x: look.x, y: look.y }} transition={eyeT}>
                  <circle cx={cx} cy="45.5" r="2.5" fill="#1B1B1B" />
                  <circle cx={cx + 0.9} cy="44.5" r="0.8" fill="#FFFFFF" />
                </motion.g>
                {isWoman && (
                  <path
                    d={cx === 41 ? "M36.5 41.5 L35 40 M38.5 40.3 L37.6 38.6" : "M63.5 41.5 L65 40 M61.5 40.3 L62.4 38.6"}
                    stroke="#1B1B1B"
                    strokeWidth="1.1"
                    strokeLinecap="round"
                  />
                )}
              </g>
            ))}
          </motion.g>
          {/* mouth */}
          <motion.path
            initial={false}
            animate={{ d: MOUTHS[mood] }}
            transition={t}
            stroke={isWoman ? c.lips : "#2A1A14"}
            strokeWidth={isWoman ? 2.6 : 2.2}
            strokeLinecap="round"
            fill={mood === "delighted" ? "#5B1F1F" : "none"}
          />
          {/* hands */}
          {(["l", "r"] as const).map((side) => (
            <motion.ellipse
              key={side}
              cx={side === "l" ? 40 : 60}
              cy="46"
              rx="9.5"
              ry="10.5"
              fill={c.skin}
              stroke="rgba(0,0,0,0.25)"
              strokeWidth="1"
              initial={false}
              animate={{ y: handY(side) }}
              transition={t}
            />
          ))}
        </g>
      </svg>
    </motion.div>
  );
};

type Tone = "info" | "success" | "warn";

const Bubble: React.FC<{ text: string; align: "left" | "right"; tone: Tone; reduceMotion: boolean }> = ({ text, align, tone, reduceMotion }) => {
  const toneCls =
    tone === "success"
      ? "bg-[#ECFDF3] border-[#A6E4BE] text-[#166534]"
      : tone === "warn"
      ? "bg-[#FFF8E6] border-[#F5C451] text-[#7A4B00]"
      : "bg-white border-[#D5DEF3] text-[#143887]";
  return (
    <div className={`absolute ${align === "left" ? "left-0 top-3 max-w-[38%]" : "right-0 top-0 max-w-[60%]"}`}>
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={text}
          initial={reduceMotion ? false : { opacity: 0, y: 6, scale: 0.94 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: -4, scale: 0.96 }}
          transition={{ duration: reduceMotion ? 0 : 0.18 }}
          className={`border shadow-sm rounded-xl px-3 py-1.5 text-[12px] leading-snug ${toneCls}`}
        >
          {text}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

/**
 * A woman and a man who keep the user company on the sign-up form. Their eyes
 * follow the mouse (or a tap on touch screens, or the text while typing), they
 * cover their eyes on the password field, and they cheer each finished field.
 * Password strength uses the same 4 rules as the checklist.
 * Decorative: hidden from screen readers, respects reduced motion.
 */
const SignupBuddies: React.FC<SignupBuddiesProps> = ({ focus, name, email, password, showPassword, checks, agreedToTerms, isCompany }) => {
  const reduceMotion = useReducedMotion() ?? false;
  const [pointer, setPointer] = useState<Pointer>(null);
  const [lastTypedAt, setLastTypedAt] = useState(0);
  const [nod, setNod] = useState(0);

  // Follow the mouse, and taps on touch screens.
  useEffect(() => {
    if (reduceMotion) return;
    let frame = 0;
    const onMove = (e: PointerEvent) => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => setPointer({ x: e.clientX, y: e.clientY, at: Date.now() }));
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerdown", onMove);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerdown", onMove);
    };
  }, [reduceMotion]);

  // Typing: nod, and look at the text until the mouse moves again.
  const typedSignature = `${name.length}|${email.length}|${password.length}`;
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    setLastTypedAt(Date.now());
    setNod((n) => n + 1);
  }, [typedSignature]);

  const metCount = Object.values(checks).filter(Boolean).length;
  const strong = metCount === 4;
  const hasPassword = password.length > 0;
  const emailValid = isValidEmail(email);
  const nameWords = name.trim().split(/\s+/).filter(Boolean);
  const nameDone = isCompany ? name.trim().length >= 2 : nameWords.length >= 2;
  const firstName = nameWords[0]?.slice(0, 14) || "";
  const allDone = nameDone && emailValid && strong;
  const covering = focus === "password";
  const peeking = covering && showPassword;

  const typed = focus === "name" ? name : focus === "email" ? email : "";
  const textLook: Look | null =
    focus === "name" || focus === "email" ? { x: -2.2 + Math.min(typed.length, 28) * (4.4 / 28), y: 2.2 } : null;
  const preferText = !pointer || lastTypedAt > pointer.at;

  const missing = MISSING_HINTS.find(([key]) => !checks[key])?.[1];
  const capital = (s?: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "");

  // What they say, and how they look while saying it.
  let side: string;
  let main: string;
  let tone: Tone = "info";
  let mood: Mood = "happy";

  if (focus === "password") {
    if (!hasPassword) {
      side = "No peeking!";
      main = "Don't worry, we're not looking 🙈";
    } else if (strong) {
      side = "🎉 Yes!";
      main = "Great job! That's a strong password 💪";
      tone = "success";
      mood = "delighted";
    } else if (metCount >= 2) {
      side = peeking ? "Just a peek 👀" : "Almost…";
      main = `Getting there, ${missing}`;
      mood = "neutral";
    } else {
      side = peeking ? "Just a peek 👀" : "😬";
      main = `Hmm, easy to guess. ${capital(missing)}`;
      tone = "warn";
      mood = "worried";
    }
  } else if (focus === "email") {
    if (!email) {
      side = firstName ? `Hi ${firstName}!` : "Oh hey!";
      main = "What's your email? ✉️";
    } else if (emailValid) {
      side = "👍";
      main = "Great job! That email looks good ✅";
      tone = "success";
      mood = "delighted";
    } else if (!email.includes("@")) {
      side = "Typing…";
      main = "Keep going, don't forget the @";
      mood = "neutral";
    } else {
      side = "Almost!";
      main = "Now add the domain, like .com";
      mood = "neutral";
    }
  } else if (focus === "name") {
    if (!name.trim()) {
      side = "Oh hey, it's you!";
      main = isCompany ? "What's your organisation called?" : "What should we call you?";
    } else if (nameDone) {
      side = "👍";
      main = isCompany ? `Great job! ${name.trim().slice(0, 18)} sounds great` : `Great job, ${firstName}! 👋`;
      tone = "success";
      mood = "delighted";
    } else {
      side = "Oh hey!";
      main = `Nice to meet you, ${firstName}! Your last name too?`;
    }
  } else if (allDone) {
    side = "🎉";
    main = agreedToTerms ? "Great job! Now hit Create Account 🚀" : "You're all set! Tick the terms to continue ✅";
    tone = "success";
    mood = "delighted";
  } else if (email && !emailValid) {
    side = "🤔";
    main = "Hmm, that email doesn't look right";
    tone = "warn";
    mood = "worried";
  } else if (hasPassword && !strong) {
    side = "Almost…";
    main = `Your password needs work: ${missing}`;
    tone = "warn";
    mood = "neutral";
  } else {
    side = "Oh hey!";
    main = firstName ? `Welcome aboard, ${firstName}!` : "Let's get you started 🚚";
  }

  // Hop each time a field is completed (or everything is ready to submit).
  const milestones = [nameDone, emailValid, strong, allDone && !!agreedToTerms].filter(Boolean).length;
  // (animation controls, not a key change, so the characters keep their state)
  const hop = useAnimationControls();
  const lastMilestones = useRef(milestones);
  useEffect(() => {
    if (milestones > lastMilestones.current && !reduceMotion) {
      hop.start({ y: [0, -12, 0, -5, 0], transition: { duration: 0.6, ease: "easeOut" } });
    }
    lastMilestones.current = milestones;
  }, [milestones, hop, reduceMotion]);

  const shared = { mood, pointer, textLook, preferText, covering, nod, reduceMotion };

  return (
    <div className="relative mx-auto w-full max-w-[300px] h-[128px] sm:h-[140px] select-none" aria-hidden="true">
      <Bubble text={side} align="left" tone={tone === "success" ? "success" : "info"} reduceMotion={reduceMotion} />
      <Bubble text={main} align="right" tone={tone} reduceMotion={reduceMotion} />
      {/* Centring lives on this wrapper: framer-motion owns the inner element's transform. */}
      <div className="absolute bottom-0 inset-x-0 flex justify-center">
        <motion.div className="flex items-end" animate={hop}>
          <div className="w-[76px] h-[76px] sm:w-[84px] sm:h-[84px] -mr-3 relative z-10">
            <Character variant="woman" peeking={peeking} {...shared} />
          </div>
          <div className="w-[76px] h-[76px] sm:w-[84px] sm:h-[84px] mb-3">
            <Character variant="man" peeking={false} {...shared} />
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default SignupBuddies;
